/**
 * Owns puzzle state: the board, the drawn path, and the rules for extending
 * or backtracking it. Renderer and PathInput are dumb by comparison — this
 * is the only place that knows what a legal move is.
 */

import { colOf, createBoard, idOf, neighborsOf, rowOf, type Board } from "../../scripts/lib/board.ts";
import type { DeepSkyEntry, Level, NodeId } from "../../scripts/lib/types.ts";
import { computeLayout, pointToCell, tileCenter, type Layout } from "./layout.ts";
import { Renderer } from "./renderer.ts";
import { PathInput, type PointerPhase } from "./input.ts";

/**
 * A touch contact point is a blob, not a pixel, and it sits under the
 * fingertip — right on top of the tile the player is trying to see. Nudging
 * the effective point upward keeps the tile visible while dragging.
 */
const TOUCH_FINGER_OFFSET_FACTOR = 0.6;
const TOUCH_FINGER_OFFSET_MAX = 40;

/**
 * How far from the path head a fresh tap still counts as grabbing it.
 *
 * Tapping the head is how every stroke begins, and a bare tile is a small
 * target on a phone — one tile is ~60px, and the head often sits in the top
 * row where there is nothing above it to aim at. Roughly one tile beyond the
 * centre makes the grabbable area about three and a half times the tile.
 *
 * Deliberately applied ONLY to "down": mid-drag the player is choosing
 * between adjacent tiles, and a radius that large would snap the line onto
 * tiles they never touched.
 */
const START_GRAB_RADIUS_FACTOR = 1.7;

/**
 * Resuming after a break, measured from the head of the existing path.
 *
 * Smaller than the start radius because the head sits among tiles the player
 * has already used, so an over-generous circle would grab the line back from
 * somewhere they were not pointing. Still well over a whole tile.
 */
const RESUME_GRAB_RADIUS_FACTOR = 1.35;

/** Breathing room, in CSS px, left around the board inside its container. */
const CONTAINER_INSET = 16;

export interface GameCallbacks {
  onProgress(filled: number, total: number): void;
  onWin(won: boolean): void;
}

/**
 * Non-visual feedback for path events — audio and haptics implement this.
 * Kept as a narrow interface (rather than the controller reaching for the
 * audio module directly) so the rules stay testable headlessly and so a
 * different feedback layer can be dropped in.
 */
export interface PathFeedback {
  advance(length: number, total: number): void;
  retreat(length: number, total: number): void;
  reset(): void;
  win(): void;
}

const SILENT_FEEDBACK: PathFeedback = {
  advance() {},
  retreat() {},
  reset() {},
  win() {},
};

export class GameController {
  private readonly renderer: Renderer;
  private readonly input: PathInput;

  private level!: Level;
  private board!: Board;
  private layout!: Layout;
  private path: NodeId[] = [];
  private dragging = false;
  private won = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly container: HTMLElement,
    private readonly callbacks: GameCallbacks,
    private readonly feedback: PathFeedback = SILENT_FEEDBACK,
  ) {
    this.renderer = new Renderer(canvas);
    this.input = new PathInput(canvas, {
      hitTest: (x, y, pointerType, phase) => this.hitTest(x, y, pointerType, phase),
      onDown: (id) => this.handleDown(id),
      onMove: (id) => this.handleMove(id),
      onUp: () => this.handleUp(),
    });
  }

  loadLevel(level: Level, deepSky: DeepSkyEntry | null = null): void {
    this.level = level;
    this.board = createBoard(level.rows, level.cols, level.holes, level.walls);
    this.path = [];
    this.dragging = false;
    this.won = false;
    this.renderer.setDeepSky(deepSky);
    this.resize();
    this.reportProgress();
  }

  /** Detach pointer listeners. Only needed if this controller is ever torn down. */
  destroy(): void {
    this.input.destroy();
  }

  restart(): void {
    const hadPath = this.path.length > 0;
    this.path = [];
    this.dragging = false;
    this.won = false;
    this.renderer.resetWinState();
    this.render();
    this.reportProgress();
    this.callbacks.onWin(false);
    if (hadPath) this.feedback.reset();
  }

  /** Re-run layout from the container's current box. Call on resize/orientation change. */
  resize(): void {
    if (!this.level) return;
    // clientWidth/clientHeight INCLUDE padding, so the space main.ts reserves
    // for the results sheet (as padding-bottom on .board-wrap) was being handed
    // straight back to the board — which then drew underneath the sheet, hiding
    // the bottom of the photograph the sheet exists to describe. Measure the
    // content box instead.
    const pad = getComputedStyle(this.container);
    const padX = parseFloat(pad.paddingLeft) + parseFloat(pad.paddingRight);
    const padY = parseFloat(pad.paddingTop) + parseFloat(pad.paddingBottom);
    const availWidth = Math.max(0, this.container.clientWidth - padX - CONTAINER_INSET * 2);
    const availHeight = Math.max(0, this.container.clientHeight - padY - CONTAINER_INSET * 2);
    this.layout = computeLayout(this.level.rows, this.level.cols, availWidth, availHeight);
    this.renderer.resize(this.layout.boardWidthPx, this.layout.boardHeightPx);
    this.render();
  }

  /** The tile a fresh tap is allowed to grab: wherever the rocket currently is. */
  private grabTarget(): NodeId {
    return this.path.length > 0 ? this.path[this.path.length - 1] : this.level.start;
  }

  private tileAt(x: number, y: number): NodeId | null {
    const cell = pointToCell(this.layout, this.level.rows, this.level.cols, x, y);
    if (!cell) return null;
    const id = idOf(this.level.cols, cell.r, cell.c);
    return this.board.present[id] ? id : null;
  }

  /**
   * Two different jobs depending on phase.
   *
   * "move": the fingertip offset applies, so the finger does not cover the
   * tile being aimed at, and the tile is taken exactly — adjacent choices
   * must stay honest.
   *
   * "down": no offset (the player is aiming at something they can see, and
   * shifting the read point upward used to push a top-row tap clean off the
   * board), plus a forgiving radius around the head so a near miss still
   * starts the stroke. Because this is recomputed on every pointerdown, the
   * generous zone re-arms by itself each time a finger lifts.
   */
  private hitTest(clientX: number, clientY: number, pointerType: string, phase: PointerPhase): NodeId | null {
    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    let y = clientY - rect.top;

    if (phase === "move") {
      if (pointerType === "touch") {
        y -= Math.min(this.layout.tileSize * TOUCH_FINGER_OFFSET_FACTOR, TOUCH_FINGER_OFFSET_MAX);
      }
      return this.tileAt(x, y);
    }

    const raw = this.tileAt(x, y);
    // An exact hit on a tile that can actually begin or resume a stroke wins
    // outright — that includes tapping back along the path to truncate it.
    const usable = raw !== null && (this.path.length > 0 ? this.path.includes(raw) : raw === this.level.start);
    if (usable) return raw;

    const target = this.grabTarget();
    const centre = tileCenter(
      this.layout,
      rowOf(this.level.cols, target),
      colOf(this.level.cols, target),
    );
    // The rocket is the one tile every player has to find before anything can
    // happen, and it is what a child aims at first, so it gets the widest
    // circle. The head of a broken line gets a smaller one.
    const factor =
      this.path.length > 0 ? RESUME_GRAB_RADIUS_FACTOR : START_GRAB_RADIUS_FACTOR;
    if (Math.hypot(x - centre.x, y - centre.y) <= this.layout.tileSize * factor) {
      return target;
    }
    return raw;
  }

  private handleDown(id: NodeId | null): void {
    if (id === null) {
      this.dragging = false;
      return;
    }

    if (this.path.length === 0) {
      // A fresh path may only begin at the level's start tile.
      if (id === this.level.start) {
        this.path = [id];
        this.dragging = true;
        this.won = false;
        this.feedback.advance(1, this.board.playable.length);
        this.render();
        this.reportProgress();
        this.renderer.pulseTile(id);
      } else {
        this.dragging = false;
      }
      return;
    }

    const idx = this.path.indexOf(id);
    if (idx !== -1) {
      // Touching a tile already on the path resumes dragging from there,
      // truncating anything drawn beyond it.
      const truncated = idx + 1 < this.path.length;
      this.path.length = idx + 1;
      this.dragging = true;
      this.won = false;
      // Landing on the current head changes nothing, so it makes no sound.
      if (truncated) this.feedback.retreat(this.path.length, this.board.playable.length);
      this.render();
      this.reportProgress();
    } else {
      this.dragging = false;
    }
  }

  private handleMove(id: NodeId | null): void {
    if (!this.dragging || id === null || this.won) return;

    const last = this.path[this.path.length - 1];
    if (id === last) return;

    // Backtracking: the pointer moved onto the second-to-last tile.
    if (this.path.length >= 2 && id === this.path[this.path.length - 2]) {
      this.path.pop();
      this.feedback.retreat(this.path.length, this.board.playable.length);
      this.render();
      this.reportProgress();
      return;
    }

    // Can't cross back over any other part of the path.
    if (this.path.includes(id)) return;

    // Must be an orthogonal neighbour with no wall in the way (holes and
    // walled-off edges are both already absent from board.nbr).
    if (!neighborsOf(this.board, last).includes(id)) return;

    this.path.push(id);
    this.feedback.advance(this.path.length, this.board.playable.length);
    this.render();
    this.reportProgress();
    this.renderer.pulseTile(id);
    this.checkWin();
  }

  private handleUp(): void {
    this.dragging = false;
  }

  /** Pop the last tile off the path. No-op on an empty path or after a win. */
  undo(): void {
    if (this.path.length === 0 || this.won) return;
    this.path.pop();
    this.dragging = false;
    this.feedback.retreat(this.path.length, this.board.playable.length);
    this.render();
    this.reportProgress();
  }

  private checkWin(): void {
    const total = this.board.playable.length;
    const complete = this.path.length === total && this.path[this.path.length - 1] === this.level.exit;
    if (complete && !this.won) {
      this.won = true;
      this.feedback.win();
      // The sound lands immediately; the visual payoff (victory lap, spin,
      // and — if this level has one — the photo reveal) plays out over the
      // next couple of seconds, and the win banner waits for it to finish.
      this.renderer.startWinAnimation(() => this.callbacks.onWin(true));
    }
  }

  private reportProgress(): void {
    this.callbacks.onProgress(this.path.length, this.board.playable.length);
  }

  private render(): void {
    this.renderer.draw({
      level: this.level,
      board: this.board,
      layout: this.layout,
      path: this.path,
      dragging: this.dragging,
    });
  }
}
